import { z } from 'zod';
import { percentualParaTaxa } from './itens-tributos.js';
import { dinheiro, isoDate, paginacao, taxa } from './primitives.js';

export const MODOS_ATUALIZACAO_CUSTO = ['AUTOMATICO', 'COM_APROVACAO'] as const;
export type ModoAtualizacaoCusto = (typeof MODOS_ATUALIZACAO_CUSTO)[number];

/**
 * Parametros da empresa (CompanyParams, SPEC 9.5.1) + padroes comerciais. Valor nunca e editado: nova vigencia
 * (append-only). Nada retroativo: `validFrom` anterior a hoje = 409 `VIGENCIA_RETROATIVA`.
 * Lucro desejado/minimo padrao: valem quando a vigencia da TABELA deixa o campo nulo (DP6).
 */
export const novoParametroSchema = z.object({
  validFrom: isoDate,
  fixedExpenseRate: taxa,
  financialCostRate: taxa,
  /** Fracao (0.2 = 20%); null/ausente = a empresa nao define padrao. */
  lucroDesejadoPadrao: taxa.nullish(),
  lucroMinimoPadrao: taxa.nullish(),
  /** Validade padrao da proposta, em dias. */
  validadeDiasPadrao: z.number().int('Use um numero inteiro').min(1, 'Minimo 1 dia').max(365, 'Maximo 365 dias').default(15),
  modoAtualizacaoCusto: z.enum(MODOS_ATUALIZACAO_CUSTO).default('COM_APROVACAO'),
  /** Pontos percentuais como fracao (0.02 = 2 p.p.). */
  limiarAlertaLucro: taxa.default('0.02'),
  /** Sessao 7: encargos sociais (fracao), dias/mes e horas/dia da empresa. Custo de pessoa = salario total x (1 + encargos), exceto terceirizado. */
  encargosSociais: taxa.default('0'),
  diasMes: dinheiro.refine((v) => Number(v) > 0 && Number(v) <= 31, 'Dias por mês entre 1 e 31').default('22'),
  horasDia: dinheiro.refine((v) => Number(v) > 0 && Number(v) <= 24, 'Horas por dia entre 0 e 24').default('8'),
  /** Sessao 9: ociosidade padrao dos centros (fracao, 0 <= x < 1). Divisor do CPM: minutos x (1 - ociosidade). */
  ociosidade: taxa.refine((v) => Number(v) < 1, 'Ociosidade menor que 100%').default('0'),
  /** Sessao 11: NEGOCIADO esconde o praticado na Formacao e na Lista (o orcamento parte do sugerido); TABELA usa a lista publicada. */
  politicaPreco: z.enum(['NEGOCIADO', 'TABELA']).default('TABELA'),
  /** Sessao 15: o custo de processo (minutos x CPM) entra no custo variavel da margem de contribuicao? Padrao nao (fixo absorvido). */
  processoCustoVariavel: z.boolean().default(false),
  /** Sessao 15 (6b, P4): CBS/IBS integram a base do ICMS no regime de transicao (2027-2032). Padrao SIM. */
  cbsIbsNaBaseIcms: z.boolean().default(true),
  /** QT18: CBS/IBS integram a base do ISS no regime de transicao. Padrao SIM. */
  cbsIbsNaBaseIss: z.boolean().default(true),
  /** QT1: a empresa cumpre as obrigacoes acessorias (em 2026, CBS/IBS sao so informativos). */
  cumpreObrigacoesAcessorias: z.boolean().default(true),
  motivo: z.string().trim().min(1).max(300).optional(),
});
export type NovoParametroDto = z.infer<typeof novoParametroSchema>;
/** Entrada do formulario (antes dos defaults do Zod). */
export type NovoParametroInput = z.input<typeof novoParametroSchema>;

const pct = z
  .string()
  .trim()
  .refine((v) => percentualParaTaxa(v) !== null, 'Percentual invalido (0 a 100, ate 6 casas)');
const pctOpcional = z
  .string()
  .trim()
  .refine((v) => v === '' || percentualParaTaxa(v) !== null, 'Percentual invalido (0 a 100, ate 6 casas)');

/**
 * Formulario "nova vigencia" dos parametros: percentuais como o usuario digita ("23", "2,5"); lucro padrao vazio = a
 * empresa nao define; limiar em pontos percentuais ("2" = 2 p.p. = 0.02). A saida e o corpo de `novoParametroSchema`
 * (fracoes), que valida de novo: fonte unica das regras.
 */
export const novoParametroFormSchema = z
  .object({
    validFrom: isoDate,
    fixedExpenseRate: pct,
    financialCostRate: pct,
    lucroDesejadoPadrao: pctOpcional,
    lucroMinimoPadrao: pctOpcional,
    validadeDiasPadrao: z.string().trim().regex(/^\d{1,3}$/, 'Use dias inteiros (1 a 365)'),
    modoAtualizacaoCusto: z.enum(MODOS_ATUALIZACAO_CUSTO),
    limiarAlertaLucro: pct,
    encargosSociais: pctOpcional.default(''),
    diasMes: z.string().trim().default(''),
    horasDia: z.string().trim().default(''),
    ociosidade: pctOpcional.default(''),
    politicaPreco: z.enum(['NEGOCIADO', 'TABELA']).default('TABELA'),
    processoCustoVariavel: z.boolean().default(false),
    cbsIbsNaBaseIcms: z.boolean().default(true),
    cbsIbsNaBaseIss: z.boolean().default(true),
    cumpreObrigacoesAcessorias: z.boolean().default(true),
    motivo: z.string().trim().max(300, 'Ate 300 caracteres'),
  })
  .transform((v): NovoParametroInput => ({
    validFrom: v.validFrom,
    fixedExpenseRate: percentualParaTaxa(v.fixedExpenseRate) ?? v.fixedExpenseRate,
    financialCostRate: percentualParaTaxa(v.financialCostRate) ?? v.financialCostRate,
    lucroDesejadoPadrao: v.lucroDesejadoPadrao === '' ? null : (percentualParaTaxa(v.lucroDesejadoPadrao) ?? v.lucroDesejadoPadrao),
    lucroMinimoPadrao: v.lucroMinimoPadrao === '' ? null : (percentualParaTaxa(v.lucroMinimoPadrao) ?? v.lucroMinimoPadrao),
    validadeDiasPadrao: Number(v.validadeDiasPadrao),
    modoAtualizacaoCusto: v.modoAtualizacaoCusto,
    limiarAlertaLucro: percentualParaTaxa(v.limiarAlertaLucro) ?? v.limiarAlertaLucro,
    encargosSociais: v.encargosSociais === '' ? '0' : (percentualParaTaxa(v.encargosSociais) ?? v.encargosSociais),
    diasMes: v.diasMes === '' ? '22' : v.diasMes.replace(',', '.'),
    horasDia: v.horasDia === '' ? '8' : v.horasDia.replace(',', '.'),
    ociosidade: v.ociosidade === '' ? '0' : (percentualParaTaxa(v.ociosidade) ?? v.ociosidade),
    politicaPreco: v.politicaPreco,
    processoCustoVariavel: v.processoCustoVariavel,
    cbsIbsNaBaseIcms: v.cbsIbsNaBaseIcms,
    cbsIbsNaBaseIss: v.cbsIbsNaBaseIss,
    cumpreObrigacoesAcessorias: v.cumpreObrigacoesAcessorias,
    ...(v.motivo ? { motivo: v.motivo } : {}),
  }))
  .pipe(novoParametroSchema);
export type NovoParametroFormEntrada = z.input<typeof novoParametroFormSchema>;

export const listarParametrosSchema = paginacao;
export type ListarParametrosQuery = z.infer<typeof listarParametrosSchema>;

export const vigenteQuerySchema = z.object({ data: isoDate });
export type VigenteQuery = z.infer<typeof vigenteQuerySchema>;

export interface ParametroEmpresaResponse {
  id: string;
  validFrom: string;
  validTo: string | null;
  fixedExpenseRate: string;
  financialCostRate: string;
  lucroDesejadoPadrao: string | null;
  lucroMinimoPadrao: string | null;
  validadeDiasPadrao: number;
  modoAtualizacaoCusto: ModoAtualizacaoCusto;
  limiarAlertaLucro: string;
  encargosSociais: string;
  diasMes: string;
  horasDia: string;
  ociosidade: string;
  politicaPreco: 'NEGOCIADO' | 'TABELA';
  processoCustoVariavel: boolean;
  cbsIbsNaBaseIcms: boolean;
  cbsIbsNaBaseIss: boolean;
  cumpreObrigacoesAcessorias: boolean;
  motivo: string | null;
  /** Quem criou a vigencia (nome), quando houver. */
  criadoPor: string | null;
  createdAt: string;
}
